import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, SkipForward, SkipBack, Volume2, Search, Music, Loader, ChevronDown, ListMusic } from 'lucide-react';
import { useSpotify } from '../context/SpotifyContext';

const SpotifyPlayer = () => {
  const {
    isAuthenticated,
    setIsAuthenticated,
    accessToken,
    setAccessToken,
    currentTrack,
    setCurrentTrack,
    isPlaying,
    setIsPlaying,
    deviceId,
    setDeviceId,
    volume,
    setVolume,
    showPlayerBar,
    setShowPlayerBar,
    setProgressMs,
    setDurationMs,
    playerRef
  } = useSpotify();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [playerReady, setPlayerReady] = useState(false);
  const [playerError, setPlayerError] = useState(null);
  const [playlists, setPlaylists] = useState([]);
  const [showPlaylistDropdown, setShowPlaylistDropdown] = useState(false);
  const [loadingPlaylists, setLoadingPlaylists] = useState(false);

  // Get base URL without /api suffix - prioritize window override
  const getBaseURL = () => {
    // Prioritize window override (set by emergent-main.js) as it's more reliable
    const envUrl = window.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || '';
    // Remove trailing /api if present
    return envUrl.replace(/\/api$/, '');
  };

  const API_URL = getBaseURL();

  // Refresh the access token using the refresh token
  const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem('spotify_refresh_token');
    if (!refreshToken) {
      console.log('No refresh token available, need to re-authenticate');
      handleLogout();
      return null;
    }

    try {
      console.log('Refreshing Spotify access token...');
      const response = await fetch(`${API_URL}/api/spotify/auth/refresh?refresh_token=${encodeURIComponent(refreshToken)}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        const newToken = data.access_token;
        localStorage.setItem('spotify_access_token', newToken);
        // Update token expiry time (typically 1 hour)
        const expiresAt = Date.now() + (data.expires_in * 1000);
        localStorage.setItem('spotify_token_expires', expiresAt.toString());
        setAccessToken(newToken);
        console.log('Token refreshed successfully');
        return newToken;
      } else {
        console.error('Failed to refresh token, status:', response.status);
        handleLogout();
        return null;
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      handleLogout();
      return null;
    }
  };

  // Check if token is expired
  const isTokenExpired = () => {
    const expiresAt = localStorage.getItem('spotify_token_expires');
    if (!expiresAt) return true;
    // Add 1 minute buffer
    return Date.now() >= (parseInt(expiresAt) - 60000);
  };

  // Get a valid access token (refresh if needed)
  const getValidToken = async () => {
    let token = localStorage.getItem('spotify_access_token');
    if (!token || isTokenExpired()) {
      token = await refreshAccessToken();
    }
    return token;
  };

  // Handle logout/clear auth
  const handleLogout = () => {
    localStorage.removeItem('spotify_access_token');
    localStorage.removeItem('spotify_refresh_token');
    localStorage.removeItem('spotify_token_expires');
    setAccessToken(null);
    setIsAuthenticated(false);
    setUserProfile(null);
    setShowPlayerBar(false);
  };

  // Check for existing token on mount
  useEffect(() => {
    const initializeSpotify = async () => {
      const token = localStorage.getItem('spotify_access_token');
      if (token) {
        // Check if token is expired
        if (isTokenExpired()) {
          console.log('Token expired on mount, attempting refresh...');
          const newToken = await refreshAccessToken();
          if (newToken) {
            setAccessToken(newToken);
            setIsAuthenticated(true);
            fetchUserProfile(newToken);
            fetchPlaylists(newToken);
            initializePlayer(newToken);
          }
        } else {
          setAccessToken(token);
          setIsAuthenticated(true);
          fetchUserProfile(token);
          fetchPlaylists(token);
          initializePlayer(token);
        }
      }
    };
    
    initializeSpotify();
    
    // Cleanup on unmount
    return () => {
      if (window.spotifyPlaybackInterval) {
        clearInterval(window.spotifyPlaybackInterval);
      }
      if (playerRef.current) {
        playerRef.current.disconnect();
      }
    };
  }, []);

  // Fetch user profile with retry on 401
  const fetchUserProfile = async (token) => {
    try {
      let response = await fetch(`${API_URL}/api/spotify/user/profile?access_token=${token}`);
      
      // If 401, try to refresh token and retry
      if (response.status === 401) {
        console.log('Profile fetch got 401, refreshing token...');
        const newToken = await refreshAccessToken();
        if (newToken) {
          response = await fetch(`${API_URL}/api/spotify/user/profile?access_token=${newToken}`);
        } else {
          return;
        }
      }
      
      if (response.ok) {
        const data = await response.json();
        setUserProfile(data);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  // Fetch user playlists (fetches all playlists)
  const fetchPlaylists = async (token) => {
    setLoadingPlaylists(true);
    try {
      const response = await fetch(`${API_URL}/api/spotify/playlists?access_token=${encodeURIComponent(token)}&fetch_all=true`);
      if (response.ok) {
        const data = await response.json();
        console.log(`🎵 Fetched ${data.items?.length || 0} playlists`);
        setPlaylists(data.items || []);
      }
    } catch (error) {
      console.error('Error fetching playlists:', error);
    } finally {
      setLoadingPlaylists(false);
    }
  };

  // Play a playlist
  const playPlaylist = async (playlistUri) => {
    setLoading(true);
    setShowPlaylistDropdown(false);
    
    try {
      const token = await getValidToken();
      if (!token) {
        alert('Not authenticated. Please reconnect Spotify.');
        return;
      }
      
      // Get available devices
      const devices = await checkAvailableDevices(token);
      let targetDeviceId = deviceId;
      
      if (!devices.find(d => d.id === deviceId) && devices.length > 0) {
        const activeDevice = devices.find(d => d.is_active) || devices[0];
        targetDeviceId = activeDevice.id;
      }
      
      console.log('🎵 Playing playlist:', playlistUri);
      
      // Play the playlist context
      const response = await fetch(
        `${API_URL}/api/spotify/playback/play-context?access_token=${encodeURIComponent(token)}&context_uri=${encodeURIComponent(playlistUri)}${targetDeviceId ? `&device_id=${encodeURIComponent(targetDeviceId)}` : ''}`,
        { method: 'POST' }
      );
      
      if (response.ok) {
        console.log('🎵 Playlist started!');
        setShowPlayerBar(true);
      } else {
        const errorText = await response.text();
        console.error('🎵 Play playlist error:', errorText);
        alert('Failed to play playlist. Make sure Spotify is open on a device.');
      }
    } catch (error) {
      console.error('🎵 Play playlist error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initialize Spotify Web Playback SDK
  const initializePlayer = (token) => {
    // Clean up existing player if any
    if (playerRef.current) {
      playerRef.current.disconnect();
      playerRef.current = null;
    }
    
    if (!window.Spotify) {
      const script = document.createElement('script');
      script.src = 'https://sdk.scdn.co/spotify-player.js';
      script.async = true;
      document.body.appendChild(script);

      window.onSpotifyWebPlaybackSDKReady = () => {
        createPlayer(token);
      };
    } else {
      createPlayer(token);
    }
  };

  const createPlayer = (token) => {
    console.log('🎵 Creating Spotify player...');
    
    const player = new window.Spotify.Player({
      name: 'DW Dashboard Player',
      getOAuthToken: async (cb) => { 
        // Get a valid token (refreshes if expired)
        const validToken = await getValidToken();
        console.log('🎵 Providing token to SDK');
        cb(validToken || token);
      },
      volume: volume / 100
    });

    // Error handling
    player.addListener('initialization_error', ({ message }) => {
      console.error('🎵 Initialization Error:', message);
    });
    
    player.addListener('authentication_error', ({ message }) => {
      console.error('🎵 Authentication Error:', message);
      setPlayerError('Authentication failed. Please reconnect Spotify.');
      setPlayerReady(false);
      // Token might be invalid, try to refresh
      refreshAccessToken();
    });
    
    player.addListener('account_error', ({ message }) => {
      console.error('🎵 Account Error:', message);
      setPlayerError('Spotify Premium is required for playback.');
      setPlayerReady(false);
    });
    
    player.addListener('playback_error', ({ message }) => {
      console.error('🎵 Playback Error:', message);
      // Don't show error for "no list was loaded" - this is expected when starting fresh
      if (!message.includes('no list was loaded')) {
        setPlayerError(`Playback error: ${message}`);
      }
    });

    // Not Ready
    player.addListener('not_ready', ({ device_id }) => {
      console.log('🎵 Device has gone offline:', device_id);
      setDeviceId(null);
      setPlayerReady(false);
      setPlayerError('Player disconnected. Click "Reconnect" to try again.');
    });

    // Ready
    player.addListener('ready', ({ device_id }) => {
      console.log('🎵 Spotify Player Ready with Device ID:', device_id);
      setDeviceId(device_id);
      setPlayerReady(true);
      setPlayerError(null);
      
      // Don't automatically transfer - let user initiate playback
      console.log('🎵 Player is ready for playback');
    });

    // Player state changed
    player.addListener('player_state_changed', (state) => {
      if (!state) return;
      
      const track = state.track_window.current_track;
      console.log('🎵 Player state changed:', {
        trackName: track?.name,
        isPaused: state.paused,
        position: state.position
      });
      
      if (track) {
        setCurrentTrack({
          name: track.name,
          artists: track.artists.map(a => a.name).join(', '),
          album: track.album.name,
          image: track.album.images[0]?.url,
          uri: track.uri
        });
        setIsPlaying(!state.paused);
        setProgressMs(state.position || 0);
        setDurationMs(state.duration || 0);
        
        // Show player bar when music is playing
        if (!state.paused) {
          console.log('🎵 Showing player bar!');
          setShowPlayerBar(true);
        }
      }
    });

    player.connect().then(success => {
      if (success) {
        console.log('🎵 Spotify Player connected successfully!');
        // Start polling for playback state to sync with other devices
        startPlaybackSync(token);
      } else {
        console.error('🎵 Failed to connect Spotify Player');
        setPlayerError('Failed to connect to Spotify. Please refresh the page.');
      }
    });
    
    playerRef.current = player;
  };

  // Poll Spotify for current playback state (syncs with all devices)
  const startPlaybackSync = (token) => {
    // Clear any existing interval
    if (window.spotifyPlaybackInterval) {
      clearInterval(window.spotifyPlaybackInterval);
    }
    
    const syncPlayback = async () => {
      try {
        const currentToken = await getValidToken();
        if (!currentToken) return;
        
        const response = await fetch(
          `${API_URL}/api/spotify/playback/state?access_token=${encodeURIComponent(currentToken)}`
        );
        
        if (response.ok) {
          const state = await response.json();
          if (state && state.item) {
            // Update current track from whatever is playing
            setCurrentTrack({
              name: state.item.name,
              artists: state.item.artists.map(a => a.name).join(', '),
              album: state.item.album.name,
              image: state.item.album.images[0]?.url,
              uri: state.item.uri
            });
            setIsPlaying(state.is_playing);
            setProgressMs(state.progress_ms || 0);
            setDurationMs(state.item.duration_ms || 0);
            
            if (state.is_playing) {
              setShowPlayerBar(true);
            }
          }
        }
      } catch (error) {
        // Silent fail - polling shouldn't show errors
        console.log('🎵 Playback sync check failed');
      }
    };
    
    // Check immediately
    syncPlayback();
    
    // Then poll every 3 seconds
    window.spotifyPlaybackInterval = setInterval(syncPlayback, 3000);
  };

  // Check available devices
  const checkAvailableDevices = async (token) => {
    try {
      const response = await fetch(
        `${API_URL}/api/spotify/devices?access_token=${encodeURIComponent(token)}`
      );
      if (response.ok) {
        const data = await response.json();
        console.log('🎵 Available devices:', data);
        return data.devices || [];
      }
    } catch (error) {
      console.error('🎵 Error checking devices:', error);
    }
    return [];
  };

  // Transfer playback to this device via backend
  const transferPlaybackToDevice = async (newDeviceId, token) => {
    try {
      console.log('🎵 Transferring playback to device:', newDeviceId);
      const API_URL = getBaseURL();
      const response = await fetch(
        `${API_URL}/api/spotify/playback/transfer?access_token=${encodeURIComponent(token)}&device_id=${encodeURIComponent(newDeviceId)}&play=false`,
        { method: 'POST' }
      );
      
      if (response.ok) {
        console.log('🎵 Playback transferred successfully');
      } else {
        const errorText = await response.text();
        console.log('🎵 Transfer response:', response.status, errorText);
      }
    } catch (error) {
      console.error('🎵 Error transferring playback:', error);
    }
  };

  // Login with Spotify
  const handleLogin = async () => {
    try {
      // Pass current origin to help backend determine correct redirect URI for production
      const currentOrigin = window.location.origin;
      const loginUrl = `${API_URL}/api/spotify/auth/login?redirect_host=${encodeURIComponent(currentOrigin)}`;
      console.log('🎵 Spotify login URL:', loginUrl);
      console.log('🎵 Current origin:', currentOrigin);
      
      const response = await fetch(loginUrl);
      const data = await response.json();
      window.location.href = data.auth_url;
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  // Search tracks with token refresh handling
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const token = await getValidToken();
      if (!token) {
        console.log('No valid token for search');
        return;
      }
      
      let response = await fetch(
        `${API_URL}/api/spotify/search?q=${encodeURIComponent(searchQuery)}&access_token=${token}&limit=10`
      );
      
      // If 401, try refresh and retry
      if (response.status === 401) {
        console.log('Search got 401, refreshing token...');
        const newToken = await refreshAccessToken();
        if (newToken) {
          response = await fetch(
            `${API_URL}/api/spotify/search?q=${encodeURIComponent(searchQuery)}&access_token=${newToken}&limit=10`
          );
        } else {
          return;
        }
      }
      
      const data = await response.json();
      setSearchResults(data.tracks?.items || []);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  };

  // Play track using backend proxy
  const playTrack = async (trackUri) => {
    console.log('🎵 playTrack called with:', trackUri);
    
    setLoading(true);
    setPlayerError(null);
    
    try {
      const token = await getValidToken();
      if (!token) {
        alert('Not authenticated. Please reconnect Spotify.');
        return;
      }
      
      // First, check available devices
      console.log('🎵 Checking available devices...');
      const devices = await checkAvailableDevices(token);
      console.log('🎵 Available devices:', devices);
      
      let targetDeviceId = deviceId;
      
      // If our SDK device isn't in the list, use any available device
      const ourDevice = devices.find(d => d.id === deviceId);
      if (!ourDevice && devices.length > 0) {
        // Use the first available device (preferably active one)
        const activeDevice = devices.find(d => d.is_active) || devices[0];
        targetDeviceId = activeDevice.id;
        console.log('🎵 Using alternative device:', activeDevice.name);
      }
      
      if (!targetDeviceId && devices.length === 0) {
        setPlayerError('No Spotify devices available');
        alert('No Spotify devices found. Please open Spotify on your phone, computer, or another device first.');
        return;
      }
      
      console.log('🎵 Playing track:', trackUri, 'on device:', targetDeviceId);
      
      // Try to play
      const response = await fetch(
        `${API_URL}/api/spotify/playback/play?access_token=${encodeURIComponent(token)}&track_uri=${encodeURIComponent(trackUri)}&device_id=${encodeURIComponent(targetDeviceId)}`,
        { method: 'POST' }
      );
      
      console.log('🎵 Play response status:', response.status);
      
      if (response.ok) {
        console.log('🎵 Playback started!');
        setShowPlayerBar(true);
        setPlayerError(null);
      } else if (response.status === 404 || response.status === 400) {
        // Device not found - try without device_id (plays on active device)
        console.log('🎵 Trying without specific device...');
        const retryResponse = await fetch(
          `${API_URL}/api/spotify/playback/play?access_token=${encodeURIComponent(token)}&track_uri=${encodeURIComponent(trackUri)}`,
          { method: 'POST' }
        );
        
        if (retryResponse.ok) {
          console.log('🎵 Playback started on active device!');
          setShowPlayerBar(true);
          setPlayerError(null);
        } else {
          setPlayerError('No active device found');
          alert('Please open Spotify on any device first, then try again.');
        }
      } else if (response.status === 403) {
        setPlayerError('Spotify Premium required');
        alert('Spotify Premium is required for web playback.');
      } else {
        const errorText = await response.text();
        console.error('🎵 Play error:', errorText);
        setPlayerError('Playback failed');
      }
    } catch (error) {
      console.error('🎵 Play error:', error);
      setPlayerError('Error playing track');
    } finally {
      setLoading(false);
    }
  };

  // Toggle play/pause using API
  const togglePlayback = async () => {
    try {
      const token = await getValidToken();
      if (!token) return;
      
      if (isPlaying) {
        await fetch(
          `${API_URL}/api/spotify/playback/pause?access_token=${encodeURIComponent(token)}`,
          { method: 'POST' }
        );
        setIsPlaying(false);
      } else {
        await fetch(
          `${API_URL}/api/spotify/playback/play?access_token=${encodeURIComponent(token)}`,
          { method: 'POST' }
        );
        setIsPlaying(true);
      }
      console.log('🎵 Toggled playback');
    } catch (error) {
      console.error('🎵 Toggle playback error:', error);
    }
  };

  // Skip to next track using API
  const skipNext = async () => {
    try {
      const token = await getValidToken();
      if (!token) return;
      
      await fetch(
        `${API_URL}/api/spotify/playback/next?access_token=${encodeURIComponent(token)}`,
        { method: 'POST' }
      );
      console.log('🎵 Skipped to next track');
    } catch (error) {
      console.error('🎵 Skip next error:', error);
    }
  };

  // Skip to previous track using API
  const skipPrevious = async () => {
    try {
      const token = await getValidToken();
      if (!token) return;
      
      await fetch(
        `${API_URL}/api/spotify/playback/previous?access_token=${encodeURIComponent(token)}`,
        { method: 'POST' }
      );
      console.log('🎵 Skipped to previous track');
    } catch (error) {
      console.error('🎵 Skip previous error:', error);
    }
  };

  // Set volume
  const handleVolumeChange = async (newVolume) => {
    setVolume(newVolume);
    if (playerRef.current) {
      await playerRef.current.setVolume(newVolume / 100);
    }
  };

  // Not authenticated view
  if (!isAuthenticated) {
    return (
      <div className="border bg-transparent border-2 border-white/60 rounded-2xl p-6 shadow-2xl shadow-[#C0C0C0]/10">
        <div className="flex flex-col items-center justify-center space-y-4">
          <Music size={48} className="text-green-500" />
          <h3 className="text-xl font-semibold text-white">Spotify Music Player</h3>
          <p className="text-white/60 text-center text-sm">
            Connect your Spotify account to play music
          </p>
          <button
            onClick={handleLogin}
            className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-full font-medium transition-colors"
          >
            Connect Spotify
          </button>
        </div>
      </div>
    );
  }

  // Check for premium
  if (userProfile && !userProfile.is_premium) {
    return (
      <div className="border bg-transparent border-2 border-white/60 rounded-2xl p-6 shadow-2xl shadow-[#C0C0C0]/10">
        <div className="flex flex-col items-center justify-center space-y-4">
          <Music size={48} className="text-yellow-500" />
          <h3 className="text-xl font-semibold text-white">Spotify Premium Required</h3>
          <p className="text-white/60 text-center text-sm">
            Web playback requires a Spotify Premium subscription
          </p>
          <a
            href="https://www.spotify.com/premium/"
            target="_blank"
            rel="noopener noreferrer"
            className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-full font-medium transition-colors"
          >
            Upgrade to Premium
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="border bg-transparent border-2 border-white/60 rounded-2xl p-4 space-y-4 shadow-2xl shadow-[#C0C0C0]/10">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Music size={24} className="text-green-500" />
          <h3 className="text-lg font-semibold text-white">Spotify Player</h3>
          {/* Device status indicator */}
          <div className={`w-2 h-2 rounded-full ${playerReady ? 'bg-green-500' : 'bg-yellow-500 animate-pulse'}`} 
               title={playerReady ? 'Player ready' : 'Connecting...'} />
        </div>
        <div className="flex items-center gap-2">
          {userProfile && (
            <span className="text-xs text-white/60">{userProfile.display_name}</span>
          )}
          {!playerReady && (
            <button
              onClick={async () => {
                const token = await getValidToken();
                if (token) {
                  setPlayerError('Reconnecting...');
                  initializePlayer(token);
                }
              }}
              className="px-3 py-1 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-500 text-xs rounded-lg transition-colors"
            >
              Reconnect
            </button>
          )}
          {/* Test button to manually show player bar */}
          {currentTrack && (
            <button
              onClick={() => {
                console.log('🎵 Manual show player bar clicked');
                setShowPlayerBar(true);
              }}
              className="px-3 py-1 bg-green-500/20 hover:bg-green-500/30 text-green-500 text-xs rounded-lg transition-colors"
            >
              Show Bar
            </button>
          )}
        </div>
      </div>

      {/* Error message */}
      {playerError && (
        <div className="px-3 py-2 bg-red-500/20 border border-red-500/30 rounded-lg">
          <p className="text-red-400 text-xs">{playerError}</p>
        </div>
      )}

      {/* Search */}
      <div className="space-y-2">
        <div className="flex gap-2">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Search songs, artists..."
            className="flex-1 px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white text-sm placeholder-white/40 focus:outline-none focus:border-green-500"
          />
          <button
            onClick={handleSearch}
            disabled={searching}
            className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            {searching ? <Loader size={16} className="animate-spin" /> : <Search size={16} />}
          </button>
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="max-h-48 overflow-y-auto space-y-1 bg-white/5 rounded-lg p-2">
            {searchResults.map((track) => (
              <div
                key={track.id}
                onClick={() => playTrack(track.uri)}
                className="flex items-center gap-3 p-2 hover:bg-white/10 rounded-lg cursor-pointer transition-colors"
              >
                <img
                  src={track.album.images[2]?.url}
                  alt={track.name}
                  className="w-10 h-10 rounded"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{track.name}</p>
                  <p className="text-white/60 text-xs truncate">
                    {track.artists.map(a => a.name).join(', ')}
                  </p>
                </div>
                <Play size={16} className="text-green-500" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Playlists Dropdown */}
      {playlists.length > 0 && (
        <div className="relative">
          <button
            onClick={() => setShowPlaylistDropdown(!showPlaylistDropdown)}
            className="w-full flex items-center justify-between px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white text-sm hover:bg-white/10 transition-colors"
          >
            <div className="flex items-center gap-2">
              <ListMusic size={16} className="text-green-500" />
              <span>My Playlists ({playlists.length})</span>
            </div>
            <ChevronDown size={16} className={`transition-transform ${showPlaylistDropdown ? 'rotate-180' : ''}`} />
          </button>
          
          {showPlaylistDropdown && (
            <div className="absolute top-full left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-black/95 border border-white/20 rounded-lg shadow-xl z-10 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
              {loadingPlaylists ? (
                <div className="p-4 text-center">
                  <Loader size={20} className="animate-spin text-green-500 mx-auto" />
                </div>
              ) : (
                playlists.map((playlist) => (
                  <div
                    key={playlist.id}
                    onClick={() => playPlaylist(playlist.uri)}
                    className="flex items-center gap-3 p-2 hover:bg-white/10 cursor-pointer transition-colors border-b border-white/5 last:border-b-0"
                  >
                    <img
                      src={playlist.images?.[0]?.url || 'https://via.placeholder.com/40x40/1a1a1a/666?text=♪'}
                      alt={playlist.name}
                      className="w-10 h-10 rounded object-cover bg-white/10 flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{playlist.name}</p>
                      <p className="text-white/60 text-xs truncate">
                        {playlist.owner?.display_name} • {playlist.tracks?.total || 0} tracks
                      </p>
                    </div>
                    <Play size={14} className="text-green-500 flex-shrink-0" />
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Current Track */}
      {currentTrack && (
        <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
          <img
            src={currentTrack.image}
            alt={currentTrack.name}
            className="w-16 h-16 rounded-lg"
          />
          <div className="flex-1 min-w-0">
            <p className="text-white font-medium truncate">{currentTrack.name}</p>
            <p className="text-white/60 text-sm truncate">{currentTrack.artists}</p>
            <p className="text-white/40 text-xs truncate">{currentTrack.album}</p>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center justify-center gap-4">
        <button
          onClick={skipPrevious}
          className="p-2 hover:bg-white/10 rounded-full transition-colors text-white"
        >
          <SkipBack size={20} />
        </button>
        <button
          onClick={togglePlayback}
          disabled={loading || !deviceId}
          className="p-4 bg-green-500 hover:bg-green-600 rounded-full transition-colors disabled:opacity-50 text-white"
        >
          {loading ? (
            <Loader size={24} className="animate-spin" />
          ) : isPlaying ? (
            <Pause size={24} />
          ) : (
            <Play size={24} />
          )}
        </button>
        <button
          onClick={skipNext}
          className="p-2 hover:bg-white/10 rounded-full transition-colors text-white"
        >
          <SkipForward size={20} />
        </button>
      </div>

      {/* Volume */}
      <div className="flex items-center gap-3">
        <Volume2 size={18} className="text-white/60" />
        <input
          type="range"
          min="0"
          max="100"
          value={volume}
          onChange={(e) => handleVolumeChange(parseInt(e.target.value))}
          className="flex-1 h-1 bg-white/20 rounded-lg appearance-none cursor-pointer"
          style={{
            background: `linear-gradient(to right, #22c55e 0%, #22c55e ${volume}%, rgba(255,255,255,0.2) ${volume}%, rgba(255,255,255,0.2) 100%)`
          }}
        />
        <span className="text-white/60 text-xs w-8 text-right">{volume}%</span>
      </div>

      {!deviceId && (
        <p className="text-yellow-500 text-xs text-center">
          Initializing player...
        </p>
      )}
    </div>
  );
};

export default SpotifyPlayer;
